import React, { useState, useEffect } from 'react';
import { 
  DollarSign, 
  TrendingUp, 
  AlertCircle, 
  UserPlus, 
  Plus, 
  X, 
  Type,
  Target,
  Zap,
  User,
  ExternalLink,
  MapPin,
  Eye
} from 'lucide-react';
import { useAdsStore } from '../../store/adsStore';
import { useGoogleAds } from '../../hooks/useGoogleAds';
import { RootState, AppDispatch } from '../../redux/index';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPracticeById } from '../../redux/practice/practiceSlice';
import useFetchClinicianByPractice from '../../hooks/Clinic/useFetchClinicianByPractice';
import Preloader from '../../components/Preloader';
import { motion, AnimatePresence } from 'framer-motion';
import { AdsCampaign } from '../../types/ads';

interface CampaignData {
  customer_id: string;
  business_name: string;
  campaign_type: string;
  campaign_name: string;
  daily_budget_micros: number;
  brand_guidelines_enabled: boolean;
  headlines: string[];
  long_headlines: string[];
  descriptions: string[];
  brand_assets: File[];
  image_assets: File[];
  text_assets: string[];
  final_url: string;
  final_url_mobile: string;
  additional_config: Record<string, unknown>;
}
import { DashboardLayout } from '../../layouts/DashboardLayout';

interface AdsManagerProps {
  practiceId: string;
}

interface ExtendedClinician {
  id: string;
  _id?: string;
  name?: string;
  firstName?: string;
  first_name?: string;
  lastName?: string;
  last_name?: string;
  email?: string;
  title?: string;
  enabled?: boolean;
  google_ads_active?: boolean;
  profile?: {
    profileImage?: string;
    bio?: string;
    specialties?: string[];
  };
  specialties?: string[] | string;
  address?: {
    city?: string;
    state?: string;
  };
}

interface ArrayFieldProps {
  label: string;
  field: 'headlines' | 'long_headlines' | 'descriptions' | 'text_assets';
  placeholder: string;
  maxLength?: number;
  icon?: React.ComponentType<{ className?: string }>;
}

// Generated assets from selected clinicians
interface ClinicianAssets {
  profileUrls: string[];
  headlines: string[];
  descriptions: string[];
  imageUrls: string[];
}

const AdsDetailsPage: React.FC<AdsManagerProps> = () => {
  const [selectedClinicians, setSelectedClinicians] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [activeTab, setActiveTab] = useState<'overview' | 'campaign'>('overview');
  const [dailyBudget, setDailyBudget] = useState(100);
  
  // Campaign form state
  const [campaignData, setCampaignData] = useState<CampaignData>({
    customer_id: '5312288547',
    business_name: 'Therapist Direct',
    campaign_type: 'PERFORMANCE_MAX',
    campaign_name: '',
    daily_budget_micros: 10000000, // $10 USD in micros
    brand_guidelines_enabled: false,
    headlines: [''],
    long_headlines: [''],
    descriptions: [''],
    brand_assets: [],
    image_assets: [],
    text_assets: [],
    final_url: '',
    final_url_mobile: '',
    additional_config: {}
  });

  const dispatch = useDispatch<AppDispatch>();
  const practiceId = localStorage.getItem('practiceId');
  const { campaigns, createCampaign } = useAdsStore();
  const { createCampaign: createGoogleCampaign } = useGoogleAds();
  const { practiceClinicians, isLoading, fetchClinicianData } = useFetchClinicianByPractice();
  const { selectedPractice } = useSelector((state: RootState) => state.practices);

  // Generate assets from selected clinicians
  const generateClinicianAssets = (): ClinicianAssets => {
    const selectedClinicianData = (practiceClinicians as ExtendedClinician[])?.filter(
      clinician => selectedClinicians.includes(clinician.id || clinician._id || '')
    ) || [];

    const baseUrl = window.location.origin;
    
    return {
      profileUrls: selectedClinicianData.map(clinician => 
        `${baseUrl}/providers/${clinician.id || clinician._id}`
      ),
      headlines: selectedClinicianData.map(clinician => {
        const name = getClinicianName(clinician);
        const title = clinician.title || 'Mental Health Professional';
        return `${name} - ${title}`;
      }),
      descriptions: selectedClinicianData.map(clinician => {
        const name = getClinicianName(clinician);
        const bio = clinician.profile?.bio || 'Experienced mental health professional dedicated to helping you achieve your wellness goals.';
        const location = clinician.address ? `${clinician.address.city}, ${clinician.address.state}` : '';
        return `${name} ${location ? `in ${location}` : ''} - ${bio.substring(0, 90)}`;
      }),
      imageUrls: selectedClinicianData
        .filter(clinician => clinician.profile?.profileImage)
        .map(clinician => clinician.profile!.profileImage!)
    };
  };

  // Initialize campaign data from practice settings
  useEffect(() => {
    if (selectedPractice) {
      setCampaignData(prev => ({
        ...prev,
        business_name: selectedPractice.name || '',
        campaign_name: `${selectedPractice.name || 'Practice'} Campaign`,
        customer_id: selectedPractice.google_ads_customer_id || '',
        daily_budget_micros: (selectedPractice.google_ads_daily_budget || 10) * 1000000,
      }));
    }
  }, [selectedPractice]);

  // Update campaign assets when selected clinicians change
  useEffect(() => {
    if (selectedClinicians.length > 0) {
      const assets = generateClinicianAssets();
      setCampaignData(prev => ({
        ...prev,
        headlines: [...prev.headlines.filter(h => h.trim()), ...assets.headlines],
        descriptions: [...prev.descriptions.filter(d => d.trim()), ...assets.descriptions],
        text_assets: [...prev.text_assets.filter(t => t.trim()), ...assets.profileUrls],
        final_url: assets.profileUrls[0] || prev.final_url
      }));
    }
  }, [selectedClinicians, practiceClinicians]);

  // Fetch practice and clinician data on mount
  useEffect(() => {
    if (practiceId) {
      fetchClinicianData(practiceId);
      dispatch(fetchPracticeById(practiceId));
    }
  }, [dispatch, fetchClinicianData, practiceId]);

  // Update selected clinicians when practice clinicians change
  useEffect(() => {
    if (Array.isArray(practiceClinicians) && practiceClinicians.length > 0) {
      setSelectedClinicians(
        (practiceClinicians as ExtendedClinician[])
          .filter(clinician => clinician?.google_ads_active)
          .map(clinician => clinician.id || clinician._id || '')
      );
    }
  }, [practiceClinicians]);

  // Show loading state
  if (isLoading) {
    return <Preloader />;
  }

  // Helper functions
  const getClinicianName = (clinician: ExtendedClinician) => {
    return clinician.name || `${clinician.firstName || clinician.first_name || ''} ${clinician.lastName || clinician.last_name || ''}`.trim() || 'Unknown Clinician';
  };

  const getClinicianInitials = (clinician: ExtendedClinician) => {
    const firstName = clinician.firstName || clinician.first_name || '';
    const lastName = clinician.lastName || clinician.last_name || '';
    return `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || 'UN';
  };

  // Form handlers
  const updateCampaignField = (field: keyof CampaignData, value: string | number | boolean | File[] | string[] | Record<string, unknown>) => {
    setCampaignData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addArrayField = (field: 'headlines' | 'long_headlines' | 'descriptions' | 'text_assets') => {
    setCampaignData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const updateArrayField = (field: 'headlines' | 'long_headlines' | 'descriptions' | 'text_assets', index: number, value: string) => {
    setCampaignData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const removeArrayField = (field: 'headlines' | 'long_headlines' | 'descriptions' | 'text_assets', index: number) => {
    setCampaignData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleFileUpload = (field: 'brand_assets' | 'image_assets', files: FileList | null) => {
    if (files) {
      setCampaignData(prev => ({
        ...prev,
        [field]: [...prev[field], ...Array.from(files)]
      }));
    }
  };

  const removeFile = (field: 'brand_assets' | 'image_assets', index: number) => {
    setCampaignData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const validateCampaignData = (): string | null => {
    if (!campaignData.customer_id.trim()) {
      return 'Customer ID is required';
    }
    if (!campaignData.business_name.trim()) {
      return 'Business name is required';
    }
    if (selectedClinicians.length === 0) {
      return 'Please select at least one clinician to advertise';
    }
    if (campaignData.headlines.filter(h => h.trim()).length === 0) {
      return 'At least one headline is required';
    }
    if (campaignData.descriptions.filter(d => d.trim()).length === 0) {
      return 'At least one description is required';
    }
    if (!campaignData.final_url.trim()) {
      return 'Final URL is required';
    }
    return null;
  };

  const handleCreateCampaign = async () => {
    const validationError = validateCampaignData();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsCreating(true);
    setError(undefined);

    try {
      // Create proper AdsCampaign object forAdvertising Partners API
      const adsCampaign: AdsCampaign = {
        id: crypto.randomUUID(),
        groupId: selectedPractice?.id || '',
        name: campaignData.campaign_name,
        status: 'active',
        budget: {
          amount: campaignData.daily_budget_micros / 1000000,
          currency: 'USD',
          period: 'daily',
        },
        selectedClinicians,
        performance: {
          impressions: 0,
          clicks: 0,
          conversions: 0,
          spend: 0,
          ctr: 0,
          cpc: 0,
          conversionRate: 0,
          costPerConversion: 0,
          profileViews: {
            total: 0,
            byClinicianId: {},
          },
        },
        startDate: new Date(),
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      // Create Google campaign
      await createGoogleCampaign(adsCampaign);
      
      // Also create in local store for UI
      const localCampaign = {
        id: crypto.randomUUID(),
        groupId: selectedPractice?.id || '',
        name: campaignData.campaign_name,
        status: 'active' as const,
        budget: {
          amount: campaignData.daily_budget_micros / 1000000,
          currency: 'USD',
          period: 'daily' as const,
        },
        selectedClinicians,
        performance: {
          impressions: 0,
          clicks: 0,
          conversions: 0,
          spend: 0,
          ctr: 0,
          cpc: 0,
          conversionRate: 0,
          costPerConversion: 0,
          profileViews: {
            total: 0,
            byClinicianId: {},
          },
        },
        startDate: new Date(),
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      createCampaign(localCampaign);
      setActiveTab('overview');
      
    } catch (err) {
      console.error('Campaign creation error:', err);
      setError('Failed to create campaign. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const updateClinicianGoogleAdsActive = async (clinicianId: string, googleAdsActive: boolean) => {
    try {
      if (googleAdsActive) {
        setSelectedClinicians(prev => [...prev, clinicianId]);
      } else {
        setSelectedClinicians(prev => prev.filter(id => id !== clinicianId));
      }
    } catch (err) {
      console.error('Failed to update clinician:', err);
      setError('Failed to update clinician. Please try again.');
    }
  };

  // Statistics
  const totalClicks = campaigns?.reduce((total, campaign) => total + (campaign.performance?.clicks || 0), 0) || 0;
  const totalImpressions = campaigns?.reduce((total, campaign) => total + (campaign.performance?.impressions || 0), 0) || 0;
  const clickRate = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(1) : '0.0';
  const spentToday = campaigns?.reduce((total, campaign) => total + (campaign.performance?.spend || 0), 0) || 0;

  // Empty state component
  const EmptyState = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="text-center py-16"
    >
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full blur-3xl opacity-30 transform scale-150"></div>
        <UserPlus className="relative mx-auto h-16 w-16 text-purple-400" />
      </div>
      <h3 className="mt-6 text-lg font-semibold text-gray-900">No clinicians found</h3>
      <p className="mt-2 text-sm text-gray-600 max-w-sm mx-auto">
        Add clinicians to your practice to start creating powerfulAdvertising Partners campaigns that drive results.
      </p>
    </motion.div>
  );

  // Array field component with glassmorphic design
  const ArrayFieldComponent: React.FC<ArrayFieldProps> = ({ 
    label, 
    field, 
    placeholder, 
    maxLength,
    icon: Icon = Type
  }) => (
    <div className="space-y-4">
      <div className="flex items-center space-x-2">
        <Icon className="h-5 w-5 text-purple-600" />
        <label className="text-sm font-semibold text-gray-900">{label}</label>
      </div>
      <div className="space-y-3">
        {campaignData[field].map((item, index) => (
          <motion.div 
            key={index} 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="group relative"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative flex items-center space-x-3 p-1">
              <input
                type="text"
                value={item}
                onChange={(e) => updateArrayField(field, index, e.target.value)}
                placeholder={placeholder}
                maxLength={maxLength}
                className="flex-1 px-4 py-3 bg-white/70 backdrop-blur-sm border border-purple-200/50 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-400 transition-all duration-300 shadow-sm hover:shadow-md"
              />
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                type="button"
                onClick={() => removeArrayField(field, index)}
                className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
              >
                <X className="h-4 w-4" />
              </motion.button>
            </div>
            {maxLength && (
              <div className="text-xs text-gray-400 mt-1 ml-4">
                {item.length}/{maxLength} characters
              </div>
            )}
          </motion.div>
        ))}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          type="button"
          onClick={() => addArrayField(field)}
          className="flex items-center justify-center w-full px-4 py-3 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-dashed border-purple-300 rounded-xl text-purple-600 hover:from-purple-100 hover:to-pink-100 hover:border-purple-400 transition-all duration-300 group"
        >
          <Plus className="h-4 w-4 mr-2 group-hover:rotate-90 transition-transform duration-300" />
          Add {label.slice(0, -1).toLowerCase()}
        </motion.button>
      </div>
    </div>
  );

  // Selected Clinicians Assets Preview
  const SelectedCliniciansPreview = () => {
    const assets = generateClinicianAssets();
    
    if (selectedClinicians.length === 0) {
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-yellow-600 mr-3" />
            <p className="text-yellow-800 font-medium">
              Select clinicians to generate campaign assets automatically
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-6">
        <h4 className="font-semibold text-green-900 mb-4 flex items-center">
          <User className="h-5 w-5 mr-2" />
          Generated Assets from {selectedClinicians.length} Selected Clinician{selectedClinicians.length !== 1 ? 's' : ''}
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Profile URLs */}
          <div>
            <h5 className="font-medium text-green-800 mb-2 flex items-center">
              <ExternalLink className="h-4 w-4 mr-1" />
              Profile URLs ({assets.profileUrls.length})
            </h5>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {assets.profileUrls.map((url, index) => (
                <div key={index} className="text-xs text-green-700 bg-green-100 px-2 py-1 rounded truncate">
                  {url}
                </div>
              ))}
            </div>
          </div>
          
          {/* Generated Headlines */}
          <div>
            <h5 className="font-medium text-green-800 mb-2 flex items-center">
              <Zap className="h-4 w-4 mr-1" />
              Headlines ({assets.headlines.length})
            </h5>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {assets.headlines.map((headline, index) => (
                <div key={index} className="text-xs text-green-700 bg-green-100 px-2 py-1 rounded">
                  {headline}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Advertising Manager</h1>
        <p className="text-gray-600">Manage your advertising campaigns and promote your clinicians</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1">{totalImpressions.toLocaleString()}</div>
          <div className="text-sm text-gray-600">Total Impressions</div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Eye className="w-5 h-5 text-blue-600" />
            </div>
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1">{totalClicks}</div>
          <div className="text-sm text-gray-600">Clicks</div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 text-purple-600" />
            </div>
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1">{clickRate}%</div>
          <div className="text-sm text-gray-600">Click Rate</div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-orange-600" />
            </div>
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1">${spentToday.toFixed(2)}</div>
          <div className="text-sm text-gray-600">Spent Today</div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Clinician Selection</h2>
              <p className="text-sm text-gray-600">Choose which clinicians to feature in your advertising campaigns</p>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700">Daily Budget:</label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">$</span>
                <input 
                  type="number" 
                  className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent" 
                  min="0" 
                  step="10" 
                  value={dailyBudget}
                  onChange={(e) => setDailyBudget(Number(e.target.value))}
                />
                <span className="text-sm text-gray-500">/day</span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            {!Array.isArray(practiceClinicians) || practiceClinicians.length === 0 ? (
              <EmptyState />
            ) : (
              (practiceClinicians as ExtendedClinician[]).map((clinician, index) => {
                const clinicianId = clinician.id || clinician._id || '';
                const isSelected = selectedClinicians.includes(clinicianId);
                const initials = getClinicianInitials(clinician);
                const name = getClinicianName(clinician);
                const specialties = Array.isArray(clinician.specialties) 
                  ? clinician.specialties.join(', ') 
                  : clinician.specialties || clinician.title || 'Mental Health Professional';
                
                return (
                  <div key={clinicianId} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:shadow-sm transition-shadow">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600 font-semibold">
                        {initials}
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">{name}</h3>
                        <p className="text-sm text-gray-600">{specialties}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-600">{isSelected ? 'Active' : 'Inactive'}</span>
                      <button 
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          isSelected ? 'bg-purple-600' : 'bg-gray-200'
                        }`}
                        onClick={() => updateClinicianGoogleAdsActive(clinicianId, !isSelected)}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          isSelected ? 'translate-x-6' : 'translate-x-1'
                        }`}></span>
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Campaign Creation Form (Hidden by default, shown when needed) */}
      {activeTab === 'campaign' && (
        <div className="mt-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            {/* <h3 className="text-lg font-semibold text-gray-900 mb-6">Create New Campaign</h3> */}
            
            {/* Selected Clinicians Assets Preview */}
            <SelectedCliniciansPreview />

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mt-6">
              {/* Basic Settings */}
              <div className="space-y-6">
                <h4 className="text-md font-semibold text-gray-900">Basic Settings</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Customer ID *</label>
                  <input
                    type="text"
                    value={campaignData.customer_id}
                    onChange={(e) => updateCampaignField('customer_id', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Enter Google Ads Customer ID"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Business Name *</label>
                  <input
                    type="text"
                    value={campaignData.business_name}
                    onChange={(e) => updateCampaignField('business_name', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Your business name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Campaign Name</label>
                  <input
                    type="text"
                    value={campaignData.campaign_name}
                    onChange={(e) => updateCampaignField('campaign_name', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Campaign name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Daily Budget (USD)</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="number"
                      min="1"
                      value={campaignData.daily_budget_micros / 1000000}
                      onChange={(e) => updateCampaignField('daily_budget_micros', Number(e.target.value) * 1000000)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="10"
                    />
                  </div>
                </div>
              </div>

              {/* URLs */}
              <div className="space-y-6">
                <h4 className="text-md font-semibold text-gray-900">Landing Pages</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Final URL *</label>
                  <input
                    type="url"
                    value={campaignData.final_url}
                    onChange={(e) => updateCampaignField('final_url', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="https://example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Mobile URL</label>
                  <input
                    type="url"
                    value={campaignData.final_url_mobile}
                    onChange={(e) => updateCampaignField('final_url_mobile', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="https://m.example.com"
                  />
                </div>
              </div>
            </div>

            {/* Ad Content */}
            <div className="mt-8">
              <h4 className="text-md font-semibold text-gray-900 mb-6">Ad Content</h4>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <ArrayFieldComponent 
                  label="Headlines *" 
                  field="headlines" 
                  placeholder="Enter compelling headline" 
                  maxLength={30}
                  icon={Zap}
                />
                
                <ArrayFieldComponent 
                  label="Descriptions *" 
                  field="descriptions" 
                  placeholder="Describe your service" 
                  maxLength={90}
                  icon={Type}
                />
              </div>
            </div>

            {/* Error Display */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="bg-red-50 border border-red-200 rounded-lg p-4 mt-6"
                >
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-red-500 mr-3" />
                    <div className="text-red-800 font-medium">{error}</div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4 mt-8">
              <button
                onClick={() => setActiveTab('overview')}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCampaign}
                disabled={isCreating}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isCreating ? 'Creating Campaign...' : 'Create Campaign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Creation Button */}
      {activeTab === 'overview' && (
        <div className="mt-8 text-center">
          {/* <button 
            className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-purple-700 transition-colors"
            onClick={() => setActiveTab('campaign')}
          >
            Create New Campaign
          </button> */}
        </div>
      )}
    </div>
    </DashboardLayout>
  );
};

export default AdsDetailsPage;